import { memo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { BILLBOARD } from 'constants/Entities';
import { BillboardModel } from 'models';
import { BillboardActions, CrudActions } from 'actions';
import EntityDetail from 'containers/components/EntityDetail';
import EntityDetailWrapper from 'containers/components/EntityDetail/EntityDetailWrapper';
import EntityDetailHeader from 'containers/components/EntityDetail/EntityDetailHeader';
import EntityDetailContent from 'containers/components/EntityDetail/EntityDetailContent';
import { CloseArrowIcon } from 'components/icons';
import EditFilledIcon from 'components/SvgIcons/EditFilled';
import { userflowHideAndShow } from 'utils/userflow';
import { getLiteral } from 'utils/getLiteral';
import useBillboardDetail from '../../hooks/useBillboardDetail';
import PanelAdmin from './components/PanelAdmin';
import PanelReader from './components/PanelReader';
import colors from 'constants/colors';
import './styles.scss';

const BillboardDetail = () => {
    const { getOptions, modalTitle, modalContent } = useBillboardDetail();
    const dispatch = useDispatch();
    const { isBillboardAdmin, isReader, isBillboardDetailOpen, isSecondaryShown, activeEntityId } =
        useSelector((state) => ({
            isBillboardAdmin: state.config.permission.adminBillboard,
            isReader: state.billboard.menu.isReader,
            isBillboardDetailOpen:
                state.entityDetail.isOpen &&
                state.entityDetail.active?.startsWith(`${BILLBOARD.entity}-`),
            isSecondaryShown: state.billboard.detail.get('showSecondary'),
            activeEntityId: state.entityDetail.active,
        }));

    useEffect(() => {
        if (isBillboardDetailOpen) {
            userflowHideAndShow('hide');
        } else {
            userflowHideAndShow('show');
        }

        return () => userflowHideAndShow('show');
    }, [isBillboardDetailOpen]);

    useEffect(() => {
        if (isSecondaryShown) dispatch(BillboardActions.hideSecondary());
    }, [activeEntityId]); // eslint-disable-line react-hooks/exhaustive-deps

    const renderContent = useCallback(
        (data) => {
            // EntityDetailManager spreads the BillboardModel instance ({ ...model }),
            // which only preserves own enumerable properties (i.e. `entity`).
            // Reconstructing a BillboardModel restores prototype getters and methods.
            const model = data?.entity ? new BillboardModel(data.entity) : data;
            if (isBillboardAdmin && !isReader) return <PanelAdmin model={model} />;
            if (isReader) return <PanelReader model={model} />;
            return null;
        },
        [isBillboardAdmin, isReader],
    );

    return (
        <EntityDetail
            entity={BILLBOARD}
            className="billboard-detail"
            customWidth="500px"
            getOptions={getOptions}
            modalTitle={modalTitle}
            modalContent={modalContent}
        >
            {({ renderLoader, loading, data, close, options, modalTitle, modalContent }) => {
                const billboardId =
                    data?.id || data?.entity?.id || activeEntityId?.split(`${BILLBOARD.entity}-`)?.[1];
                const editOption = {
                    el: <EditFilledIcon color={colors.grey[500]} />,
                    action: () => {
                        if (!billboardId) return;
                        dispatch(CrudActions.initCrud(BILLBOARD, billboardId));
                    },
                    tooltip: getLiteral('action_edit'),
                    className: 'fm-entity-detail__option-edit',
                };
                const detailOptions = isSecondaryShown
                    ? []
                    : (options || []).map((option) => (option === 'edit' ? editOption : option));
                const handleClose = isSecondaryShown
                    ? () => dispatch(BillboardActions.hideSecondary())
                    : close;
                return (
                    <EntityDetailWrapper>
                        <EntityDetailHeader
                            close={handleClose}
                            entity={BILLBOARD}
                            data={data}
                            loading={loading}
                            options={detailOptions}
                            modalTitle={modalTitle}
                            modalContent={modalContent}
                            customCloseIcon={
                                isSecondaryShown ? <CloseArrowIcon className="arrow" /> : undefined
                            }
                        />
                        <EntityDetailContent>
                            {loading && renderLoader()}
                            {!loading && data && renderContent(data)}
                        </EntityDetailContent>
                    </EntityDetailWrapper>
                );
            }}
        </EntityDetail>
    );
};

export default memo(BillboardDetail);
